import { useFrame } from '@react-three/fiber'
import { useRef } from 'react'
import { store } from '@/src/stores/store'
import type {
    FootstepSide,
    FootstepTimelineRef,
    FootstepTimelineSnapshot,
} from './footstepConfig'

const previewDefaultInterval = 0.28
const previewLeavesInterval = 0.1
const previewRunSpeed = 3.5

export function usePreviewTimeline(): FootstepTimelineRef {
    const cadenceElapsedRef = useRef(0)
    const nextSideRef = useRef<FootstepSide>('left')
    const stepIdRef = useRef(0)
    const clockRef = useRef(0)

    const timelineRef = useRef<FootstepTimelineSnapshot>({
        isActive: false,
        footstepsEnabled: false,
        runSpeed: 0,
        animationSpeed: 1,
        isJumping: false,
        steps: [],
        jumps: [],
    })

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        const showConfiguratorEffectPreview =
            store.appFlowPhase === 'configurator' &&
            store.configuratorActiveTab === 'effect' &&
            store.footstepEffect !== ''

        if (!showConfiguratorEffectPreview) {
            cadenceElapsedRef.current = 0
            timelineRef.current = {
                isActive: false,
                footstepsEnabled: false,
                runSpeed: 0,
                animationSpeed: 1,
                isJumping: false,
                steps: [],
                jumps: [],
            }
            return
        }

        clockRef.current += safeDelta
        cadenceElapsedRef.current += safeDelta

        const interval =
            store.footstepEffect === 'leaves'
                ? previewLeavesInterval
                : previewDefaultInterval

        const steps = []
        while (cadenceElapsedRef.current >= interval) {
            cadenceElapsedRef.current -= interval
            steps.push({
                id: ++stepIdRef.current,
                side: nextSideRef.current,
                timestamp: clockRef.current,
            })
            nextSideRef.current =
                nextSideRef.current === 'left' ? 'right' : 'left'
        }

        timelineRef.current = {
            isActive: true,
            footstepsEnabled: true,
            runSpeed: previewRunSpeed,
            animationSpeed: 1,
            isJumping: false,
            steps,
            jumps: [],
        }
    })

    return timelineRef
}
